import infoIcon from "@assets/actions/info.svg";
import { NEWSLETTER_MESSAGES } from "@config/newsletter";
import fetchBHApi from "@helper/fetchBHApi";
import { getValidEmailRegex } from "@helper/utils";
import { NewsletterPostResponse } from "@typings/Newsletter";
import cx from "classnames";
import type { ReactElement } from "react";
import { FormEvent, useState } from "react";
import { twMerge } from "tailwind-merge";

import styles from "./NewsletterSignupForm.module.css";

type Props = Readonly<{
  testId?: string;
}>;
export default function SignupForm({
  testId = "newsletter-form",
}: Props): ReactElement {
  const [email, setEmail] = useState<string>("");
  const [isMarketingChecked, setIsMarketingChecked] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [successMsg, setSuccessMsg] = useState<string>("");

  async function handleOnSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    e.stopPropagation();
    const isEmailValid = email && getValidEmailRegex().test(email);

    if (!isEmailValid) {
      setErrorMsg(NEWSLETTER_MESSAGES.INVALID_EMAIL);
      return;
    }

    try {
      const queryParams = new URLSearchParams({
        email,
        isMarketingChecked: String(isMarketingChecked),
      }).toString();
      const res = await fetchBHApi(`newsletterSignup?${queryParams}`, "GET");
      const data = res as NewsletterPostResponse;
      if (data.msg) {
        setErrorMsg(""); // unset error message
        setSuccessMsg(data.msg);
      } else if (data.errorMsg) {
        setErrorMsg(data.errorMsg);
      }
    } catch (e: unknown) {
      setErrorMsg(NEWSLETTER_MESSAGES.FAILED);
      return;
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };
  const handleMarketingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsMarketingChecked(e.target.checked);
  };
  return (
    <form
      onSubmit={(e: FormEvent<HTMLFormElement>) => void handleOnSubmit(e)}
      data-testid={testId}
      className="mb-md"
    >
      <div
        className={cx(
          errorMsg || successMsg ? styles.alert : "",
          errorMsg ? styles.alertError : successMsg ? styles.alertSuccess : "",
        )}
      >
        {errorMsg || successMsg}
      </div>
      <div className={styles.formGroup}>
        <input
          id="enter-email"
          name="enter-email"
          onChange={handleInputChange}
          type="email"
          className={styles.formControl}
          placeholder="Enter your email address"
        />
      </div>
      <p className={cx(styles.privacy, "w-12/12 pl-0")}>
        Dengan mendaftar, saya menerima{" "}
        <a href="https://sph.com.sg/legal/website_tnc">Terma & Syarat</a> dan{" "}
        <a href="https://sph.com.sg/legal/sph_privacy">Dasar Privasi</a> SPH
        Media, sebagaimana yang dipinda dari semasa ke semasa.
      </p>
      <div className="flex items-center my-4 space-x-2">
        <input
          name="consent-email"
          id="consent-email"
          type="checkbox"
          onChange={handleMarketingChange}
          className="appearance-auto h-3 w-3 text-blue-400 border-blue-400 rounded focus:ring-blue-500"
        />
        <label
          htmlFor="consent-email"
          className="lg:flex gap-1 items-center text-xs text-black-800"
        >
          Ya, saya juga ingin mendapatkan komunikasi pemasaran dan promosi SPH
          Media Group.
          <div className="relative group inline-block">
            {/* Info icon */}
            <img
              src={infoIcon}
              alt="info"
              width={12}
              className="cursor-pointer"
            />

            {/* Tooltip Desktop*/}
            <div
              className={`${styles.tooltipBg} absolute z-10 -top-12 lg:top-1/2 lg:left-full -translate-y-1/2 rounded-md lg:ml-1
                w-[192px] h-[97.5px] lg:w-[210px] lg:h-[92px] px-2 pt-2 pb-[19.5px] lg:py-[6px] lg:pl-[18px] lg:pr-[6px]
                invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-300`}
            >
              <div className="w-[176px] lg:w-[186px] text-black-800 text-xxs lg:text-[13px] leading-[150%]">
                SPH Media Limited, perbadanan dan sekutunya yang berkaitan serta
                ejen mereka dan pembekal perkhidmatan yang diberi kuasa.
              </div>
            </div>
          </div>
        </label>
      </div>
      <button
        className={twMerge(
          styles.btnNewsletter,
          "text-xxs w-full lg:w-[218px] h-[35px]",
        )}
        aria-label="Sign Up for Newsletter"
      >
        Langgan
      </button>
    </form>
  );
}
